import { toast } from 'react-hot-toast';

import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@src/design-system/popover';

import { useHasPermission } from '@shared/hooks/use-has-permission';
import ArrowSquareDown from '@shared/assets/icons/shared/arrow-square-down';
import { entities } from '@shared/constants/entities';

import type { TUser } from '@shared/types/user';
import { SUBMISSION_STATUS, type TSubmission } from '@shared/types/submission';
import { AnimatedTransition } from '@src/design-system/animated-transition';
import InfiniteScroll from '@src/design-system/infinite-scroll';
import { SearchInput } from '@src/design-system/molecules/search-input';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { cn } from '@src/design-system/helpers';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '@src/design-system/checkbox';
import submissionApi from '../../../../services/submission-api';
import memberApi from '@shared/services/member-api';
import type { PopoverContentProps } from '@radix-ui/react-popover';

const AssigneeName = ({ assignee }: { assignee?: TUser }) => {
  if (!assignee) return null;
  const fullName = assignee.first_name + ' ' + assignee.last_name;
  return <span className='font-medium '>{fullName}</span>;
};

function Assignees({
  onSelect,
  selectedUser,
  submissionId,
  useMemberId,
}: {
  onSelect?: (assigneeId: string | null) => void;
  selectedUser?: TUser;
  submissionId?: number;
  useMemberId?: boolean;
}) {
  const { search } = useLocation();
  const queryParams = qs.parse(search, { ignoreQueryPrefix: true });

  const { data, fetchNextPage, isPending, hasNextPage } =
    memberApi.useFetchInfinite({
      name: queryParams.memberName as string,
      type: 'EMPLOYEE',
    });
  const members = data?.pages.flatMap((p) => p.results);

  const { mutate: editSubmission } = submissionApi.useUpdate();
  const handleChange = async (newId: string, isSelected: boolean) => {
    const nextAssignee = isSelected ? null : newId;

    if (onSelect) {
      onSelect(isSelected ? null : newId);
      return;
    }

    if (submissionId) {
      editSubmission({ id: submissionId, assignee: nextAssignee });
    }
  };

  const skeleton = (
    <div className='flex justify-center h-full items-center'>
      <Loader2
        className='animate-spin text-primary-1000'
        size={24}
      />
    </div>
  );

  return (
    <>
      <AnimatedTransition
        animateOnEveryRender
        isPending={isPending}
        skeleton={<div className='h-[300px]'>{skeleton}</div>}
      >
        <div
          className=' p-4 pt-0 h-[300px] overflow-auto'
          style={{
            scrollbarWidth: 'none',
          }}
        >
          {!isPending && !members?.length && (
            <div className='flex h-full justify-center items-center'>
              <span className='text-center text-sm'>No results found.</span>
            </div>
          )}

          <div className='flex flex-col'>
            {members
              ?.filter((m) => m.user)
              .map((m) => {
                const user = m.user;
                const name = `${user.first_name} ${user.last_name}`;

                const isSelected = useMemberId
                  ? m.id === selectedUser?.id
                  : user.id === selectedUser?.id;

                return (
                  <PopoverClose
                    key={m.id}
                    className='mb-2 last:mb-0'
                  >
                    <div
                      key={m.id}
                      onClick={() =>
                        handleChange(
                          useMemberId ? String(m.id) : String(user.id),
                          isSelected
                        )
                      }
                      className='z-10 flex border border-pale-border text-sm text-start w-full p-2 hover:bg-primary-30 rounded-lg gap-2 items-center cursor-pointer'
                    >
                      <Checkbox
                        className={cn(
                          'rounded-full',
                          isSelected &&
                            'relative border-0 bg-orange-400 after:size-2 after:bg-white after:absolute after:rounded-full after:inset-1/2 after:-translate-1/2 '
                        )}
                      />
                      <div className='flex min-w-0 gap-2'>
                        <Avatar className='size-10'>
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback colorId={m.id}>
                            {user.first_name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className='flex min-w-0 flex-col'>
                          <span>{name}</span>
                          <span className='text-xs text-gray-500 truncate min-w-0'>
                            {user.email}
                          </span>
                        </div>
                      </div>
                    </div>
                  </PopoverClose>
                );
              })}
          </div>
        </div>
      </AnimatedTransition>

      <InfiniteScroll
        hasMore={hasNextPage}
        threshold={0.8}
        isLoading={isPending}
        next={fetchNextPage}
      >
        {hasNextPage && (
          <div className='w-full flex items-center justify-center mt-4'>
            <Loader2
              className='animate-spin text-primary-1000'
              size={24}
            />
          </div>
        )}
      </InfiniteScroll>
    </>
  );
}

export function AssigneeList({
  submission,
  showTrigger = true,
  onSelect,
  customTrigger,
  popoverContentProps,
  useMemberId,
}: {
  submission?: TSubmission & { manager_member?: TUser };
  showTrigger?: boolean;
  onSelect?: (assigneeId: string | null) => void;
  customTrigger?: React.ReactNode;
  popoverContentProps?: PopoverContentProps;
  useMemberId?: boolean;
}) {
  const hasPermission = useHasPermission();
  const canViewMembers = hasPermission(entities.member, 'view');

  const selectedUser = submission?.assignee || submission?.manager_member;

  const trigger =
    customTrigger ||
    (showTrigger ? (
      <div className='flex items-center gap-1 hover:bg-primary-50 rounded-lg cursor-pointer p-2 text-sm'>
        Assigned to
        <AssigneeName assignee={selectedUser} />
        <ArrowSquareDown />
      </div>
    ) : (
      <div className='flex items-center gap-2 cursor-pointer'>
        {selectedUser ? (
          <>
            <Avatar className='h-[19px] w-[19px] rounded-sm'>
              <AvatarImage
                src={selectedUser.avatar}
                alt='Assignee Avatar'
              />
              <AvatarFallback className='text-xs rounded-sm'>
                {selectedUser.first_name?.[0]}
                {selectedUser.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <span className='!text-sm text-gray-900 whitespace-nowrap'>
              {`${selectedUser.first_name} ${selectedUser.last_name}`}
            </span>
          </>
        ) : (
          <span className='size-5 flex justify-center items-center rounded-full border border-dashed border-pale-border'>
            +
          </span>
        )}
      </div>
    ));

  if (!canViewMembers) {
    return (
      <span
        onClick={() => toast.error("You don't have access to view members")}
      >
        {trigger}
      </span>
    );
  }

  const isWithdrawn = submission?.status === SUBMISSION_STATUS.withdrawn;

  return (
    <Popover>
      <PopoverTrigger
        disabled={isWithdrawn}
        className='flex items-center gap-1 text-sm border-0 shadow-none'
      >
        {!selectedUser && isWithdrawn ? 'Not assigned yet' : trigger}
      </PopoverTrigger>

      <PopoverContent
        align='center'
        side='right'
        className='p-0'
        {...popoverContentProps}
      >
        <PopoverHeader>Assignee to</PopoverHeader>

        <div className='p-4'>
          <SearchInput queryKey='memberName' />
        </div>

        <Assignees
          onSelect={onSelect}
          selectedUser={selectedUser}
          submissionId={submission?.id}
          useMemberId={useMemberId}
        />
      </PopoverContent>
    </Popover>
  );
}
